import { useContentProgress } from 'ContentProgress';
import { useCurrentUser } from 'FrontRoyalAngular';
import { useStudentDashboardContent } from 'StudentDashboardContent';
import { getProgramInclusion, getCohort } from 'Users';
import { useMemo } from 'react';

export function usePastDueCoursesAndProjects() {
    const currentUser = useCurrentUser();
    const cohort = useMemo(() => getCohort(currentUser), [currentUser]);
    const programInclusion = useMemo(() => getProgramInclusion(currentUser), [currentUser]);

    // Get dashboard content and progress data for past-due calculations with stable references
    const studentDashboardContent = useStudentDashboardContent();
    const { streamProgressByLocalePackId } = useContentProgress();
    // Calculate past-due courses based on cohort periods and stream completion
    const allPastDueCourses = useMemo(() => {
        if (!cohort || !studentDashboardContent.lessonStreams) {
            return [];
        }

        // If user is in week 0, no courses are past due
        if (cohort.currentPeriodIndex === -1) {
            return [];
        }

        // Determine which periods to check for past-due courses
        // If cohort has ended, currentPeriodIndex will be null
        const periodsToCheck =
            cohort.currentPeriodIndex === null ? cohort.periods.length : cohort.currentPeriodIndex - 1;

        // Extract required stream pack IDs from relevant periods
        const requiredStreamPackIds = cohort.periods
            .slice(0, periodsToCheck + 1)
            .flatMap(period => period.streamEntries)
            .filter(entry => entry.required)
            .map(entry => entry.localePackId);

        // Filter streams to only include required and incomplete ones
        return studentDashboardContent.lessonStreams.filter(stream => {
            const streamProgress = streamProgressByLocalePackId[stream.localePackId];
            return requiredStreamPackIds.includes(stream.localePackId) && !streamProgress?.complete;
        });
    }, [cohort, studentDashboardContent.lessonStreams, streamProgressByLocalePackId]);

    // Get past-due projects from program inclusion data with stable reference
    const allPastDueProjects = useMemo(
        () => programInclusion?.pastDueProjects || [],
        [programInclusion?.pastDueProjects],
    );

    return useMemo(() => ({ allPastDueCourses, allPastDueProjects }), [allPastDueCourses, allPastDueProjects]);
}
